import { useEffect, useState } from "react"

import { useQueryParam } from "web/hooks/context"
import { isNonEmptyArray, distinctBy, unique } from "web/services/helpers"

import { getMapBoxSafeValues } from "./common"

import LocationMap from "./LocationMap"
import SaleAssetLocationCard from "./LocationCard/SaleAssetLocationCard"

export default function SaleAssetAssetMap({
  saleAssets,
  onSelectionChanged,
  isLoading,
  showOrdinals,
}) {
  const [locationInFocus, setLocationInFocus] = useState()
  const hasLocationInFocus = !!locationInFocus

  const saleAssetLocations = saleAssets.reduce((locations, saleAsset) => {
    const saleAssetValues = getMapBoxSafeValues(saleAsset.values)

    if (isNonEmptyArray(saleAsset.locations)) {
      locations.push(
        ...saleAsset.locations.map((al) => ({
          ...al,
          sticky: saleAsset.sticky,
          comparable_deal_id: saleAsset.id,
          values: saleAssetValues,
        }))
      )
    }

    return locations
  }, [])

  const distinctSaleAssetLocations = distinctBy(saleAssetLocations, "id")

  const locationInFocusWithSubjects = { location: locationInFocus }

  if (hasLocationInFocus) {
    const saleAssetsAtLocation = saleAssets.filter((saleAsset) =>
      saleAsset.locations?.some(
        (location) => location.id === locationInFocus.id
      )
    )

    const assetIdsAtLocation = saleAssetsAtLocation
      .map((saleAsset) => saleAsset.locations)
      .flat()
      .filter((location) => location.id === locationInFocus.id)
      .map((location) => location.asset_id)

    const distinctAssetIdsAtLocation = unique(assetIdsAtLocation)

    locationInFocusWithSubjects.saleAssets = saleAssetsAtLocation
    locationInFocusWithSubjects.assetIds = distinctAssetIdsAtLocation
  }

  const primarySubject =
    hasLocationInFocus && locationInFocusWithSubjects.saleAssets[0]

  const hasPrimarySubject = !!primarySubject

  // deal in focus may have been removed between
  // renders by change in filter conditions
  useEffect(() => {
    if (hasLocationInFocus && !hasPrimarySubject) {
      setLocationInFocus(undefined)
    }
  }, [hasLocationInFocus, hasPrimarySubject])

  const selected = saleAssets.filter((el) => el.selected)

  const center_on = useQueryParam("center_on")
  const centralLocation =
    // eslint-disable-next-line eqeqeq
    !!center_on &&
    distinctSaleAssetLocations.find(
      (location) => location.comparable_deal_id == center_on
    )

  return (
    <LocationMap
      subjectType="sale_asset"
      subjects={saleAssets}
      primarySubject={primarySubject}
      selected={selected}
      locations={distinctSaleAssetLocations}
      locationInFocus={locationInFocusWithSubjects}
      centralLocation={centralLocation}
      onLocationClick={setLocationInFocus}
      onSelectionChanged={onSelectionChanged}
      detailCard={SaleAssetLocationCard}
      isLoading={isLoading}
      canRecenterMap={true}
      showOrdinals={showOrdinals}
    />
  )
}
